import { Supplier } from './supplierConfig'

export type ProductId = 'PROSPA_BLBG' | 'FINANCE_ONE_CAPG' | 'BANJO_SMELBG'

export const ProductName = {
  PROSPA_BLBG: 'Business Loan Bank Guarantee',
  FINANCE_ONE_CAPG: 'Property Loan Bank Guarantee',
  BANJO_SMELBG: 'SME Loan Bank Guarantee'
} as const
export type ProductName = typeof ProductName[keyof typeof ProductName]

export const InterestRateType = {
  TD: 'TD',
  CMA: 'CMA',
  VARIABLE: 'VARIABLE'
} as const
export type InterestRateType = typeof InterestRateType[keyof typeof InterestRateType]

export const OngoingFeeType = {
  ASR: 'ASR',
  APR: 'APR'
} as const
export type OngoingFeeType = typeof OngoingFeeType[keyof typeof OngoingFeeType]

export interface ProductConfig {
  /** Product name - set in admin portal */
  name: ProductName
  /** Issuing bank  */
  supplier: Supplier
  /** Non bank supplier / indirect guarantor e.g. Prospa, Finance One */
  nonBankSupplierName?: string
  nonBankSupplierShort?: string
  hasProductTermsModal: boolean
  securityTypeDisplay: string
  ongoingFee: number
  ongoingFeeDescription: string
  ongoingFeeType?: OngoingFeeType
  originationFee: string
  interestRateType?: InterestRateType
  consentInformationUrl: string
  privacyPolicyUrl: string
  interestCharged: string
  interestChargedDescription: string
  interestEarned?: string
}

export type ProductConfigs = Record<ProductId, ProductConfig>

export const PRODUCT_CONFIGS: ProductConfigs = {
  PROSPA_BLBG: {
    name: ProductName.PROSPA_BLBG,
    supplier: 'BNK',
    nonBankSupplierName: 'Prospa Advance Pty Ltd',
    nonBankSupplierShort: 'Prospa',
    hasProductTermsModal: true,
    securityTypeDisplay: 'Finance',
    ongoingFee: 2.25,
    ongoingFeeDescription: 'The ongoing fee is waived during the loan period.',
    ongoingFeeType: OngoingFeeType.ASR,
    originationFee: '5.9%',
    interestRateType: InterestRateType.CMA,
    consentInformationUrl: 'https://www.prospa.com/consent-information',
    privacyPolicyUrl: 'https://www.prospa.com/privacy-policy',
    interestCharged: '7.95% - 8.95%',
    interestChargedDescription:
      'Interest charged by Prospa. The interest rates above are expressed as an annual simple rate of interest. Annual Simple Rate is flat calculation of interest over the loan term. The loan contract will disclose the equivalent Annual Percentage Rate (APR) which is used to calculate interest on a reducing balance.',
    interestEarned: '0%'
  },

  FINANCE_ONE_CAPG: {
    name: ProductName.FINANCE_ONE_CAPG,
    supplier: 'BNK',
    nonBankSupplierName: 'Finance One Commercial Pty Ltd',
    nonBankSupplierShort: 'Finance One',
    hasProductTermsModal: true,
    securityTypeDisplay: 'Property Finance',
    ongoingFee: 0,
    ongoingFeeDescription: 'BNK does not charge an ongoing bank guarantee fee for this product.',
    ongoingFeeType: OngoingFeeType.APR,
    originationFee: '4.0%',
    interestRateType: InterestRateType.CMA,
    consentInformationUrl: 'https://financeone.com.au/media/c1re44cn/finance-one-credit-reporting-policy.pdf',
    privacyPolicyUrl: 'https://financeone.com.au/privacy-policy/',
    interestCharged: 'from 9.95%',
    interestChargedDescription:
      'Interest charged by Finance One. The interest rate above is expressed as an annual percentage rate of interest. The actual interest rate charged is subject to a credit assessment.'
  },
  BANJO_SMELBG: {
    name: ProductName.BANJO_SMELBG,
    supplier: 'BNK',
    nonBankSupplierName: 'Fund2Market Pty Ltd',
    nonBankSupplierShort: 'Banjo Loans',
    hasProductTermsModal: true,
    securityTypeDisplay: 'Finance',
    ongoingFee: 1.75,
    ongoingFeeDescription: 'Charged by BNK annually in advance.',
    ongoingFeeType: OngoingFeeType.APR,
    originationFee: '4.0%',
    interestRateType: InterestRateType.CMA,
    consentInformationUrl: 'https://banjoloans.com/terms-conditions/',
    privacyPolicyUrl: 'https://banjoloans.com/privacy-policy/',
    interestCharged: 'from 8.45%',
    interestChargedDescription:
      'Interest charged by Fund2Market Pty Ltd (trading as Banjo Loans). The actual interest rate charged is subject to a credit assessment.'
  }
}

export const PRODUCT_CONFIGS_BY_NAME: Record<ProductName, ProductConfig> = Object.values(PRODUCT_CONFIGS).reduce(
  (acc, product) => {
    acc[product.name] = product
    return acc
  },
  {} as Record<ProductName, ProductConfig>
)

export const getProductConfigByName = (name?: string): ProductConfig | undefined => {
  if (!name) return

  return PRODUCT_CONFIGS_BY_NAME[name as ProductName]
}
